import { areaModules, type AccessArea } from "../constants/accessAreas.constants";
import { ACCESS_CATALOG, ATOMIC_ACTIONS } from "../constants/accessCatalog.constants";
import type { AccessPolicy, AccessRole, AtomicAction } from "../types/access.types";
import { expandAction } from "./denyFirst";

/**
 * The permission board's model: the catalog as `(module, atom)` cells, and which roles or policies stand for
 * exactly one of them.
 *
 * The RBAC seed creates one base role and one base policy per cell (`cards_read` grants `cards:read` and
 * nothing else). Listed one per row that is ~90 near-identical entries; drawn as the cell they grant, they
 * fit a module row with three toggles. Anything that grants more than one cell, or that denies, is not a cell
 * and stays a row of its own — collapsing it onto a single toggle would hide what it really does.
 */

/** `${moduleId}|${atom}` — `|` never appears in a Module id (which uses `:`). */
export const cellKey = (resource: string, atom: string) => `${resource}|${atom}`;

export interface PermissionCell {
  moduleId: string;
  atom: AtomicAction;
}

const CATALOG_IDS = new Set<string>(ACCESS_CATALOG.map((mod) => mod.id));

/** `cards_topup_sensitive` → `cards:topup:sensitive`, built from the catalog (ids contain `_` too). */
const MODULE_BY_SLUG_STEM = new Map<string, string>(
  ACCESS_CATALOG.map((mod) => [mod.id.replace(/:/g, "_"), mod.id]),
);

/**
 * The one cell a set of policies grants, or `null` when it is not exactly one allowed catalog cell (several
 * cells, any deny, an uncatalogued resource, or nothing at all).
 */
export function singleAllowedCell(policies: readonly AccessPolicy[]): PermissionCell | null {
  let found: PermissionCell | null = null;
  for (const policy of policies) {
    if (policy.effect !== "allow") return null;
    for (const statement of policy.statements) {
      if (!CATALOG_IDS.has(statement.resource)) return null;
      for (const atom of expandAction(statement.action)) {
        if (found && (found.moduleId !== statement.resource || found.atom !== atom)) return null;
        found = { moduleId: statement.resource, atom };
      }
    }
  }
  return found;
}

/**
 * The cell a role stands for. Statements decide; the slug is read only for a GLOBAL role with no statements
 * at all, the same rule as `resolveRoleGroup` — an organization's slug is derived from a free-text name and
 * says nothing about what the role grants.
 */
export function roleCell(role: AccessRole): PermissionCell | null {
  const hasStatements = role.policies.some((policy) => policy.statements.length > 0);
  if (hasStatements) return singleAllowedCell(role.policies);
  if (role.organization_id) return null;
  for (const atom of ATOMIC_ACTIONS) {
    const suffix = `_${atom}`;
    if (!role.slug.endsWith(suffix)) continue;
    const moduleId = MODULE_BY_SLUG_STEM.get(role.slug.slice(0, -suffix.length));
    return moduleId ? { moduleId, atom } : null;
  }
  return null;
}

export interface CellIndex<T> {
  /** Items that stand for exactly one cell, keyed by {@link cellKey}. Canonical item first. */
  byCell: Map<string, T[]>;
  /** Everything else, in the input order. */
  rest: T[];
}

/**
 * Splits items into those a toggle can stand for and the rest. When several items grant the same cell, the
 * Coloca-owned one goes first: it is what the toggle adds, since every organization sees the same one.
 */
export function indexByCell<T extends { organization_id?: string | null }>(
  items: readonly T[],
  cellOf: (item: T) => PermissionCell | null,
): CellIndex<T> {
  const byCell = new Map<string, T[]>();
  const rest: T[] = [];
  for (const item of items) {
    const cell = cellOf(item);
    if (!cell) {
      rest.push(item);
      continue;
    }
    const key = cellKey(cell.moduleId, cell.atom);
    const list = byCell.get(key) ?? [];
    if (item.organization_id == null) list.unshift(item);
    else list.push(item);
    byCell.set(key, list);
  }
  return { byCell, rest };
}

/** The shortcuts an area offers. */
export type AreaLevel = "none" | "read" | "readWrite" | "all";

export const AREA_LEVELS: readonly AreaLevel[] = ["none", "read", "readWrite", "all"];

const ATOMS_BY_LEVEL: Record<AreaLevel, ReadonlySet<AtomicAction>> = {
  none: new Set(),
  read: new Set(["read"]),
  readWrite: new Set(["read", "write"]),
  all: new Set(["read", "write", "delete"]),
};

export interface CellTarget extends PermissionCell {
  on: boolean;
}

/**
 * What applying a level to an area means, cell by cell. Sensitive modules are only ever switched OFF by a
 * shortcut ("none"): they exist precisely so that seeing card numbers is granted on purpose, one tick at a
 * time, never as a side effect of "let them see Cards".
 */
export function areaLevelTargets(area: AccessArea, level: AreaLevel): CellTarget[] {
  const atoms = ATOMS_BY_LEVEL[level];
  const out: CellTarget[] = [];
  for (const mod of areaModules(area)) {
    if (mod.sensitive && level !== "none") continue;
    for (const atom of mod.actions) out.push({ moduleId: mod.id, atom, on: atoms.has(atom) });
  }
  return out;
}

/**
 * Which shortcut the area currently matches, given what is ticked. Only cells a toggle can change take part
 * (`isToggleable`); `null` means a mix no shortcut describes.
 */
export function currentAreaLevel(
  area: AccessArea,
  isOn: (cell: PermissionCell) => boolean,
  isToggleable: (cell: PermissionCell) => boolean,
): AreaLevel | null {
  const sensitiveOn = areaModules(area).some(
    (mod) =>
      mod.sensitive &&
      mod.actions.some(
        (atom) => isToggleable({ moduleId: mod.id, atom }) && isOn({ moduleId: mod.id, atom }),
      ),
  );
  for (const level of AREA_LEVELS) {
    if (level === "none" && sensitiveOn) continue;
    const targets = areaLevelTargets(area, level).filter(isToggleable);
    if (targets.length === 0) continue;
    if (targets.every((target) => isOn(target) === target.on)) return level;
  }
  return null;
}
